import React, { useCallback, useEffect, useRef, useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { Toast } from "primereact/toast";
import SubscriptionService from "../../services/SubscriptionService";
import {
  SaPageShell,
  SaPageHeader,
  SaCard,
  SaPrimaryButton,
  SaGhostButton,
  SaField,
  SaSectionTitle,
  saInputSx,
  SA,
} from "../shared/SuperAdminUi";

function planDurationMonths(p) {
  const n = p?.durationMonths ?? p?.DurationMonths;
  const v = typeof n === "number" ? n : parseInt(n, 10);
  return Number.isFinite(v) && v >= 1 ? v : 12;
}

function SubscriptionPlansSettings() {
  const toast = useRef(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDurationMonths, setNewDurationMonths] = useState(12);
  const [newSuggestedPrice, setNewSuggestedPrice] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingMonths, setEditingMonths] = useState(12);
  const [editingSuggested, setEditingSuggested] = useState("");

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await SubscriptionService.getAllSubscriptionPlansForManagement();
      setPlans(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error(e);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not load subscription plans.",
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const onCreate = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) {
      toast.current?.show({ severity: "warn", summary: "Name required", detail: "Enter a plan name.", life: 2500 });
      return;
    }
    const months = Math.min(240, Math.max(1, parseInt(String(newDurationMonths), 10) || 12));
    const spRaw = newSuggestedPrice.trim();
    let suggestedPrice = null;
    if (spRaw !== "") {
      const n = Number(spRaw);
      if (Number.isNaN(n)) {
        toast.current?.show({ severity: "warn", summary: "Invalid price", detail: "Enter a number or leave blank.", life: 2500 });
        return;
      }
      suggestedPrice = n;
    }
    setSaving(true);
    try {
      await SubscriptionService.createSubscriptionPlan({
        subscriptionPlanName: name,
        durationMonths: months,
        suggestedPrice,
      });
      toast.current?.show({ severity: "success", summary: "Created", detail: "Plan added.", life: 2500 });
      setNewName("");
      setNewDurationMonths(12);
      setNewSuggestedPrice("");
      await loadPlans();
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not create plan.",
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditingName(p.subscriptionPlanName ?? "");
    setEditingMonths(planDurationMonths(p));
    setEditingSuggested(p.suggestedPrice != null && p.suggestedPrice !== "" ? String(p.suggestedPrice) : "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
    setEditingMonths(12);
    setEditingSuggested("");
  };

  const saveEdit = async (id) => {
    const name = editingName.trim();
    if (!name) {
      toast.current?.show({ severity: "warn", summary: "Name required", detail: "Plan name cannot be empty.", life: 2500 });
      return;
    }
    const months = Math.min(240, Math.max(1, parseInt(String(editingMonths), 10) || 12));
    const spRaw = editingSuggested.trim();
    let suggestedPrice = null;
    if (spRaw !== "") {
      const n = Number(spRaw);
      if (Number.isNaN(n)) {
        toast.current?.show({ severity: "warn", summary: "Invalid price", detail: "Enter a number or leave blank.", life: 2500 });
        return;
      }
      suggestedPrice = n;
    }
    setSaving(true);
    try {
      await SubscriptionService.updateSubscriptionPlan(id, {
        subscriptionPlanName: name,
        durationMonths: months,
        suggestedPrice,
      });
      toast.current?.show({ severity: "success", summary: "Saved", detail: "Plan updated.", life: 2500 });
      cancelEdit();
      await loadPlans();
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not update plan.",
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const reactivate = async (p) => {
    setSaving(true);
    try {
      await SubscriptionService.reactivateSubscriptionPlan(p.id);
      toast.current?.show({ severity: "success", summary: "Reactivated", detail: "Plan is available in pickers again.", life: 2500 });
      await loadPlans();
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not reactivate plan.",
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this plan? It will disappear from subscription pickers; existing subscriptions keep their record."))
      return;
    setSaving(true);
    try {
      await SubscriptionService.deleteSubscriptionPlan(id);
      toast.current?.show({ severity: "success", summary: "Updated", detail: "Plan deactivated.", life: 2500 });
      await loadPlans();
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not deactivate plan.",
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SaPageShell>
      <Toast ref={toast} />
      <SaPageHeader
        title="Subscription plans"
        subtitle="Define product plans. Assign them when editing a client under the Subscription tab."
      />

      <TabView>
        <TabPanel header="Plans">
          <SaCard>
            <SaSectionTitle iconClass="fas fa-list">
              All plans&nbsp;
              <span style={{ fontWeight: "500", fontSize: "13px", color: SA.muted }}>(Super Admin)</span>
            </SaSectionTitle>
            <p style={{ margin: "-6px 0 18px", fontSize: "13px", color: SA.muted }}>
              Active plans appear in client subscription forms. Deactivated plans are hidden from pickers.
            </p>
            {loading ? (
              <p style={{ color: SA.muted }}>Loading…</p>
            ) : plans.length === 0 ? (
              <p style={{ color: SA.muted }}>No plans yet. Use the &quot;Create plan&quot; tab to add one.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="table mb-0" style={{ width: "100%", minWidth: "720px" }}>
                  <thead style={{ background: SA.bgSoft }}>
                    <tr>
                      {["Plan name", "Months", "Suggested price", "Status", "Actions"].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: "12px 14px",
                            fontSize: "11px",
                            fontWeight: "800",
                            color: SA.muted,
                            textTransform: "uppercase",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((p) => (
                      <tr key={p.id}>
                        <td style={{ padding: "10px 14px", verticalAlign: "middle" }}>
                          {editingId === p.id ? (
                            <input
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              style={{ ...saInputSx, width: "100%", maxWidth: "260px" }}
                            />
                          ) : (
                            <span style={{ fontWeight: "600", color: SA.ink }}>{p.subscriptionPlanName}</span>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px", verticalAlign: "middle" }}>
                          {editingId === p.id ? (
                            <input
                              type="number"
                              min={1}
                              max={240}
                              value={editingMonths}
                              onChange={(e) => setEditingMonths(e.target.value)}
                              style={{ ...saInputSx, width: "88px" }}
                            />
                          ) : (
                            <span style={{ color: SA.muted }}>{planDurationMonths(p)} mo</span>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px", verticalAlign: "middle" }}>
                          {editingId === p.id ? (
                            <input
                              type="number"
                              step="0.01"
                              placeholder="Optional"
                              value={editingSuggested}
                              onChange={(e) => setEditingSuggested(e.target.value)}
                              style={{ ...saInputSx, width: "130px" }}
                            />
                          ) : (
                            <span style={{ color: SA.muted }}>
                              {p.suggestedPrice != null && p.suggestedPrice !== "" ? p.suggestedPrice : "—"}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span
                            style={{
                              padding: "3px 10px",
                              borderRadius: "20px",
                              fontSize: "11px",
                              fontWeight: "700",
                              background: p.isActive ? "#e8f5e9" : "#fdecea",
                              color: p.isActive ? SA.success : SA.danger,
                            }}
                          >
                            {p.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          {editingId === p.id ? (
                            <>
                              <SaPrimaryButton
                                type="button"
                                disabled={saving}
                                onClick={() => saveEdit(p.id)}
                                iconClass="fas fa-check"
                              >
                                Save
                              </SaPrimaryButton>
                              <SaGhostButton type="button" disabled={saving} onClick={cancelEdit}>
                                Cancel
                              </SaGhostButton>
                            </>
                          ) : (
                            <>
                              <SaGhostButton type="button" disabled={saving} onClick={() => startEdit(p)}>
                                <i className="fas fa-pen" style={{ marginRight: "6px" }} />
                                Edit
                              </SaGhostButton>
                              {p.isActive ? (
                                <SaGhostButton type="button" disabled={saving} onClick={() => deactivate(p.id)}>
                                  <i className="fas fa-ban" style={{ marginRight: "6px" }} />
                                  Deactivate
                                </SaGhostButton>
                              ) : (
                                <SaPrimaryButton
                                  type="button"
                                  disabled={saving}
                                  onClick={() => reactivate(p)}
                                  iconClass="fas fa-check"
                                >
                                  Reactivate
                                </SaPrimaryButton>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SaCard>
        </TabPanel>

        <TabPanel header="Create plan">
          <SaCard>
            <SaSectionTitle iconClass="fas fa-plus-circle">New plan</SaSectionTitle>
            <form onSubmit={onCreate}>
              <SaField label="Plan name *">
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Enterprise yearly"
                  style={{ ...saInputSx, maxWidth: "420px", width: "100%" }}
                />
              </SaField>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "18px", marginTop: "12px" }}>
                <SaField label="Duration (months) *">
                  <input
                    type="number"
                    min={1}
                    max={240}
                    value={newDurationMonths}
                    onChange={(e) => setNewDurationMonths(e.target.value)}
                    style={{ ...saInputSx, width: "120px" }}
                  />
                </SaField>
                <SaField label="Suggested price (optional)">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Prefill on client form"
                    value={newSuggestedPrice}
                    onChange={(e) => setNewSuggestedPrice(e.target.value)}
                    style={{ ...saInputSx, width: "160px" }}
                  />
                </SaField>
              </div>
              <div style={{ marginTop: "16px" }}>
                <SaPrimaryButton type="submit" disabled={saving} iconClass={saving ? "fas fa-spinner fa-spin" : "fas fa-save"}>
                  {saving ? "Saving…" : "Create plan"}
                </SaPrimaryButton>
              </div>
            </form>
          </SaCard>
        </TabPanel>
      </TabView>
    </SaPageShell>
  );
}

export default SubscriptionPlansSettings;
